import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, NodeKey } from 'lexical';
import { useEffect, useRef } from 'react';
import { $isSuggestionNode, $sourceSuggestion, SuggestionNode } from '../nodes/SuggestionNode';
import { Source } from '../types';
import { scrollToNode } from '../utils/scrollToNode';

type TrackedSuggestion = {
  key: NodeKey;
  source: Source;
  textContent: string;
  scrollIntoView: () => void;
};

export type GroupedSuggestions = Record<Source, Record<NodeKey, TrackedSuggestion>>;

type Props = {
  onSuggestionsChange: (suggestions: GroupedSuggestions) => void;
};

export function SuggestionTrackerPlugin({ onSuggestionsChange }: Props) {
  const [editor] = useLexicalComposerContext();
  const onSuggestionsChangeRef = useRef(onSuggestionsChange);

  useEffect(() => {
    onSuggestionsChangeRef.current = onSuggestionsChange;
  }, [onSuggestionsChange]);

  useEffect(() => {
    const grouped: GroupedSuggestions = { default: {}, note: {} };

    return editor.registerMutationListener(
      SuggestionNode,
      mutations => {
        editor.getEditorState().read(() => {
          for (const [key, type] of mutations) {
            if (type === 'destroyed') {
              delete grouped.default[key];
              delete grouped.note[key];
            } else if (type === 'created') {
              const node = $getNodeByKey(key);
              if ($isSuggestionNode(node)) {
                const source = $sourceSuggestion(node);
                grouped[source][key] = {
                  key,
                  source,
                  textContent: node.getTextContent(),
                  scrollIntoView: () => scrollToNode(editor, key),
                };
              }
            }
          }

          onSuggestionsChangeRef.current({
            default: { ...grouped.default },
            note: { ...grouped.note },
          });
        });
      },
      { skipInitialization: false },
    );
  }, [editor]);

  return null;
}
